import React, { PropsWithChildren, ReactNode } from 'react'
import './platform-integration-card.scss'

import classnames from 'classnames'

import { DomainIntegrationProviders } from '@pushly/models/lib/enums'
import { DisconnectOutlined, LinkOutlined } from '@ant-design/icons'
import { Button, Card, Skeleton } from 'antd'

export type PlatformIntegrationCardMode = 'add' | 'edit'

type PlatformIntegrationCardAction = {
    className?: string
    loading?: boolean
    title?: string
    icon?: ReactNode | null
    onClick?: () => void
}

type BasePlatformIntegrationCardProps = {
    className?: string
    logo: ReactNode
    mode: PlatformIntegrationCardMode
    loading?: boolean
    provider: DomainIntegrationProviders
    actions?: {
        primary?: PlatformIntegrationCardAction
        secondary?: Omit<PlatformIntegrationCardAction, 'title'> & { title: string }
    }
}

type AddPlatformIntegrationCardProps = BasePlatformIntegrationCardProps & {
    mode: 'add'
}

type EditPlatformIntegrationCardProps = BasePlatformIntegrationCardProps & {
    mode: 'edit'
    isActive?: boolean
}

type PlatformIntegrationCardProps = AddPlatformIntegrationCardProps | EditPlatformIntegrationCardProps

export const PlatformIntegrationCard: React.FC<PropsWithChildren<PlatformIntegrationCardProps>> = (props) => {
    const { className, loading, mode, provider, logo, actions, children } = props

    const isAddIntegrationMode = mode === 'add'
    const isActive = mode === 'edit' ? props.isActive ?? false : false

    const defaultPrimaryAction = {
        title: isAddIntegrationMode ? 'New Connection' : 'Remove',
        icon: isAddIntegrationMode ? <LinkOutlined /> : <DisconnectOutlined />,
    }

    const primaryAction: PlatformIntegrationCardAction = {
        className: actions?.primary?.className,
        loading: actions?.primary?.loading,
        title: actions?.primary?.title ?? defaultPrimaryAction.title,
        icon: actions?.primary?.icon === null ? null : actions?.primary?.icon ?? defaultPrimaryAction.icon,
        onClick: actions?.primary?.onClick,
    }

    return (
        <Card
            className={classnames(
                'platform-integration-card',
                `integration-provider-${provider}`,
                `mode-${mode}-integration`,
                'text--content--main',
                className,
                {
                    active: mode === 'edit' && isActive,
                    empty: isAddIntegrationMode,
                },
            )}
            title={
                <Skeleton
                    loading={loading}
                    active={loading}
                    paragraph={false}
                    title={false}
                    avatar={{
                        shape: 'circle',
                        size: 24,
                    }}
                >
                    {!loading && logo}
                </Skeleton>
            }
            size="small"
            extra={
                <div className="platform-integration-card-actions">
                    {!loading && actions?.secondary && (
                        <Button
                            className={classnames(
                                'platform-integration-card-action',
                                'secondary-action',
                                actions.secondary.className,
                            )}
                            loading={actions.secondary.loading}
                            size="small"
                            // loading={loading || value.loading}
                            icon={actions.secondary.icon === null ? null : actions.secondary.icon}
                            onClick={actions.secondary.onClick}
                        >
                            <span>{actions.secondary.title}</span>
                        </Button>
                    )}
                    <Skeleton
                        loading={loading}
                        active={loading}
                        paragraph={{
                            className: 'platform-integration-card-action-skeleton',
                            rows: 1,
                        }}
                        title={false}
                        avatar={false}
                    >
                        {!loading && (
                            <Button
                                className={classnames(
                                    'platform-integration-card-action',
                                    'primary-action',
                                    primaryAction.className,
                                )}
                                loading={primaryAction.loading}
                                size="small"
                                // loading={loading || value.loading}
                                icon={primaryAction.icon}
                                onClick={primaryAction.onClick}
                            >
                                <span>{primaryAction.title}</span>
                            </Button>
                        )}
                    </Skeleton>
                </div>
            }
        >
            {!loading && !isAddIntegrationMode && (
                <div className="platform-integration-card-content text--size--small">{children}</div>
            )}
        </Card>
    )
}
